import { assert } from './assert.js';

/**
 * Convert
 * Javascript string (16-bit) to UTF-8 (8-bit) bytes
 */
export function convertStringToBytes(input: string): Uint8Array {
  assert(
    typeof input === 'string',
    'Invalid input argument: not a valid string.',
  );
  const encoder = new TextEncoder();
  return encoder.encode(input);
}

/**
 * Convert
 * UTF-8 (8-bit) bytes to javascript string (16-bit)
 */
export function convertBytestoString(input: Uint8Array): string {
  assert(
    input instanceof Uint8Array,
    'Invalid input argument: not a valid Uint8Array.',
  );
  const encoder = new TextDecoder();
  return encoder.decode(input);
}

/**
 * Convert
 * UTF-8 (8-bit) bytes to hex string
 */
export function convertBytesToHex(input: Uint8Array): string {
  assert(
    input instanceof Uint8Array,
    'Invalid input argument: not a valid Uint8Array.',
  );
  const hexArray = [];
  for (const byte of input) {
    hexArray.push(byte.toString(16).padStart(2, '0'));
  }
  return hexArray.join('');
}

/**
 * Convert
 * hex string to UTF-8 (8-bit) bytes
 */
export function convertHexToBytes(input: string): Uint8Array {
  assert(
    typeof input === 'string',
    'Invalid input argument: not a valid string.',
  );
  const inputArray = input.match(/[a-fA-F0-9]{2}/g) || [];
  const bytesArray = [];
  for (const char of inputArray) {
    const byte = Number.parseInt(char, 16);
    assert(Number.isInteger(byte) && byte >= 0, 'Invalid byte parsed.');
    bytesArray.push(byte);
  }
  return Uint8Array.from(bytesArray);
}

/**
 * Convert
 * UTF-8 (8-bit) bytes to base64 string
 */
export function convertBytesToBase64(input: Uint8Array): string {
  assert(
    input instanceof Uint8Array,
    'Invalid input argument: not a valid Uint8Array.',
  );
  const stringArray = [];
  for (const byte of input) {
    stringArray.push(String.fromCharCode(byte));
  }
  return btoa(stringArray.join(''));
}

/**
 * Convert
 * base64 string to UTF-8 (8-bit) bytes
 */
export function convertBase64ToBytes(input: string): Uint8Array {
  assert(
    typeof input === 'string',
    'Invalid input argument: not a valid string.',
  );
  const string = atob(input);
  const bytesArray = [];
  for (let idx = 0; idx < string.length; idx++) {
    bytesArray.push(string.charCodeAt(idx));
  }
  return Uint8Array.from(bytesArray);
}

/**
 * Convert
 * UTF-8 (8-bit) bytes to base64url string
 */
export function convertBytesToBase64URL(input: Uint8Array): string {
  const base64 = convertBytesToBase64(input);
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

/**
 * Convert
 * base64url string to UTF-8 (8-bit) bytes
 */
export function convertBase64URLToBytes(input: string): Uint8Array {
  let base64 = input.replace(/-/g, '+').replace(/_/g, '/');

  // Padding
  const mod = base64.length % 4;
  if (mod !== 0) {
    assert(
      mod !== 1,
      'Invalid base64 input: mod cannot be 1 according to RFC4648.',
    );
    if (mod === 2) {
      base64 += '==';
    } else if (mod === 3) {
      base64 += '=';
    }
  }
  return convertBase64ToBytes(base64);
}
